import { useQuery } from '@tanstack/react-query';
import { getExchangeRates } from '../services/rates/api';
import type { ExchangeRate } from '../services/rates/types';
import { useOfflineSupport } from './useOfflineSupport';

export function useExchangeRates() {
  const { isOnline, storeDataForOffline, getOfflineData } = useOfflineSupport();

  return useQuery<ExchangeRate[], Error>({
    queryKey: ['exchangeRates'],
    queryFn: async () => {
      if (!isOnline) {
        const offlineData = getOfflineData();
        if (offlineData) {
          return offlineData;
        }
        throw new Error('No offline data available');
      }
      
      const rates = await getExchangeRates();
      // Store data for offline use
      await storeDataForOffline(rates);
      return rates;
    },
    refetchInterval: 60000, // Refetch every minute
    staleTime: 30000, // 30 seconds stale time
    gcTime: 120000, // 2 minutes garbage collection
    retry: 2, // Reduced retries to avoid spam
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    networkMode: 'offlineFirst', // Support offline-first strategy
    // Enable background refetching for live updates
    refetchIntervalInBackground: false, // Disable background refetch to reduce errors
  });
}